import React, { useEffect, useMemo, useState } from 'react';
import { Card } from '../../components/ui/Card';
import { useAuth } from '../../context/AuthContext';
import { RefreshCw, Target, Download, Info, TrendingUp, TrendingDown, CalendarDays } from 'lucide-react';
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid,
  Tooltip as RTooltip, Legend,
} from 'recharts';

/**
 * High5 — günlük Hedef vs Gerçekleşen.
 *
 * Sütun düzeni markanın kendi Excel'iyle birebir: her metrik için
 * Hedef | Gerçekleşen | HGO (hedef gerçekleşme oranı).
 * Hedefler backend'de (routes/high5DailyTargets.js), gerçekleşen GA4'ten.
 */

const API_BASE = 'https://api.glowytics.com';

const fmtInt = (n) => (n == null ? '—' : Number(n).toLocaleString('tr-TR'));
const fmtMoney = (n) => (n == null ? '—' : `${Math.round(Number(n)).toLocaleString('tr-TR')} ₺`);
const fmtMoneyShort = (n) => {
  if (n == null) return '—';
  const v = Number(n);
  if (Math.abs(v) >= 1000000) return `${(v / 1000000).toLocaleString('tr-TR', { maximumFractionDigits: 2 })}M ₺`;
  if (Math.abs(v) >= 1000) return `${Math.round(v / 1000).toLocaleString('tr-TR')}K ₺`;
  return `${Math.round(v)} ₺`;
};
const fmtCr = (n) => (n == null ? '—' : `%${Number(n).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const fmtHgo = (n) => (n == null ? '—' : `%${Number(n).toLocaleString('tr-TR', { maximumFractionDigits: 1 })}`);
const shortDate = (d) => (d ? d.slice(8, 10) + '.' + d.slice(5, 7) : '—');
const longDate = (d) => {
  if (!d) return '—';
  try {
    return new Date(`${d}T00:00:00`).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' });
  } catch { return d; }
};

// Tablodaki dört metrik — hücre düzeni hepsinde aynı, biçimlendirici değişir.
const METRICS = [
  { k: 'sessions', f: fmtInt },
  { k: 'transactions', f: fmtInt },
  { k: 'cr', f: fmtCr },
  { k: 'revenue', f: fmtMoney },
];

/** Ay hedefinin ne kadarı tamamlandı (ayın tamamı satırı için). */
const pctOfMonth = (actual, target) => (target > 0 ? Math.round((actual / target) * 1000) / 10 : null);

/** HGO rengi — %100+ yeşil, %90-100 sarı, altı kırmızı. */
const hgoTone = (v) => {
  if (v == null) return 'text-gray-300';
  if (v >= 100) return 'text-emerald-700';
  if (v >= 90) return 'text-amber-600';
  return 'text-red-600';
};
const hgoChip = (v) => {
  if (v == null) return 'bg-gray-50 text-gray-300';
  if (v >= 100) return 'bg-emerald-50 text-emerald-700';
  if (v >= 90) return 'bg-amber-50 text-amber-700';
  return 'bg-red-50 text-red-700';
};

function KpiCard({ title, target, actual, hgo, format, sub, bqTarget, bqHgo, bqPct }) {
  const f = format || fmtInt;
  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-4">
      <div className="text-[11px] uppercase tracking-wide text-gray-400 font-semibold">{title}</div>
      <div className="mt-1 flex items-baseline gap-2">
        <div className="text-2xl font-bold text-gray-900 tabular-nums">{f(actual)}</div>
        <div className={`text-sm font-bold tabular-nums ${hgoTone(hgo)}`}>
          {hgo == null ? '' : (hgo >= 100
            ? <TrendingUp className="w-3.5 h-3.5 inline mb-0.5" />
            : <TrendingDown className="w-3.5 h-3.5 inline mb-0.5" />)} {fmtHgo(hgo)}
        </div>
      </div>
      <div className="mt-1 text-xs text-gray-500 tabular-nums">Hedef {f(target)}</div>
      {bqTarget != null && (
        <div className="text-xs text-indigo-500 tabular-nums">
          BQ hedefi (−%{bqPct}) {f(bqTarget)} · <strong className={hgoTone(bqHgo)}>{fmtHgo(bqHgo)}</strong>
        </div>
      )}
      {sub && <div className="mt-2 pt-2 border-t border-gray-100 text-xs text-gray-500">{sub}</div>}
    </div>
  );
}

export default function High5DailyTargetTracking() {
  const { selectedBrand } = useAuth();
  const [month, setMonth] = useState('');
  const [view, setView] = useState('daily'); // 'daily' | 'weekly'
  const [bqPct, setBqPct] = useState(10);     // BQ'nun eksik saydığı varsayılan oran (%)
  const [showBq, setShowBq] = useState(true); // "BQ hedefi" sütunları görünsün mü
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [matrixMonths, setMatrixMonths] = useState(6);
  const [matrix, setMatrix] = useState(null);
  const [matrixLoading, setMatrixLoading] = useState(false);
  const [matrixError, setMatrixError] = useState('');

  const fetchData = async (m, pct = bqPct) => {
    if (!selectedBrand?.id) return;
    try {
      setLoading(true);
      setError('');
      const qs = `?adjust=${pct}${m ? `&month=${m}` : ''}`;
      const res = await fetch(`${API_BASE}/api/high5-daily-targets${qs}`, {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-Brand-ID': selectedBrand.id },
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d?.error || `HTTP ${res.status}`);
      }
      const j = await res.json();
      setData(j);
      setMonth(j.month);
    } catch (e) {
      setData(null);
      setError(e?.message || 'Veri alınamadı');
    } finally {
      setLoading(false);
    }
  };

  const fetchMatrix = async (n) => {
    if (!selectedBrand?.id) return;
    try {
      setMatrixLoading(true);
      setMatrixError('');
      const res = await fetch(`${API_BASE}/api/high5-daily-targets/revenue-matrix?months=${n}`, {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-Brand-ID': selectedBrand.id },
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d?.error || `HTTP ${res.status}`);
      }
      setMatrix(await res.json());
    } catch (e) {
      setMatrix(null);
      setMatrixError(e?.message || 'Veri alınamadı');
    } finally {
      setMatrixLoading(false);
    }
  };

  useEffect(() => {
    fetchData(month || undefined);
    fetchMatrix(matrixMonths);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBrand?.id]);

  const days = useMemo(() => data?.days || [], [data]);
  const mtd = data?.mtd;
  const monthTarget = data?.monthTarget;

  // Grafik: gün gün ciro (hedef vs gerçekleşen) + kümülatif iki çizgi.
  const chartData = useMemo(() => {
    let ct = 0;
    return days.map((d) => {
      ct += d.target.revenue;
      return {
        label: shortDate(d.date),
        date: d.date,
        'Hedef ciro': d.target.revenue,
        'Gerçekleşen ciro': d.actual ? d.actual.revenue : null,
        'Kümülatif hedef': ct,
        'Kümülatif gerçekleşen': d.cum ? d.cum.actual.revenue : null,
      };
    });
  }, [days]);

  // Haftalık kırılım — ISO haftası (Pzt başlangıç). Ayın başındaki/sonundaki
  // yarım haftalar kendi satırı olarak kalır, komşu aya taşınmaz.
  //
  // 🔑 Verisi HENÜZ gelmemiş günler hedeften de düşülüyor: yürüyen haftada
  // 3 günün gerçekleşeni varsa hedef de o 3 günün hedefi. Yoksa HGO yapay
  // olarak düşük çıkar. Satırda "3/7 gün" rozeti bunu söyler.
  const weeks = useMemo(() => {
    const out = [];
    let cur = null;
    for (const d of days) {
      if (!cur || d.dow === 1) {
        cur = { days: [], first: d.date, last: d.date };
        out.push(cur);
      }
      cur.days.push(d);
      cur.last = d.date;
    }
    return out.map((w, i) => {
      const withData = w.days.filter((d) => d.actual);
      const sum = (arr, pick) => arr.reduce((a, d) => a + pick(d), 0);
      // Hedef: yalnız verisi gelmiş günlerin hedefi (hafta tamamlandıysa hepsi).
      const base = withData.length ? withData : [];
      const tS = sum(base, (d) => d.target.sessions);
      const tT = sum(base, (d) => d.target.transactions);
      const tR = sum(base, (d) => d.target.revenue);
      const aS = sum(withData, (d) => d.actual.sessions);
      const aT = sum(withData, (d) => d.actual.transactions);
      const aR = sum(withData, (d) => d.actual.revenue);
      const fullTarget = {
        sessions: sum(w.days, (d) => d.target.sessions),
        transactions: sum(w.days, (d) => d.target.transactions),
        revenue: sum(w.days, (d) => d.target.revenue),
      };
      const tCr = tS > 0 ? Math.round((tT / tS) * 10000) / 100 : null;
      const aCr = aS > 0 ? Math.round((aT / aS) * 10000) / 100 : null;
      const pct = (a, t) => (t > 0 && a != null ? Math.round((a / t) * 1000) / 10 : null);
      const has = withData.length > 0;
      // BQ hedefi = hedef × (1 − oran). CR'de etkisiz: pay da payda da kırpılır.
      const f = 1 - (data?.bqAdjustPct ?? bqPct) / 100;
      const bt = has
        ? { sessions: tS * f, transactions: tT * f, cr: tCr, revenue: tR * f }
        : { sessions: fullTarget.sessions * f, transactions: fullTarget.transactions * f,
            cr: fullTarget.sessions > 0 ? Math.round((fullTarget.transactions / fullTarget.sessions) * 10000) / 100 : null,
            revenue: fullTarget.revenue * f };
      return {
        key: `w${i}`,
        label: `${shortDate(w.first)} – ${shortDate(w.last)}`,
        sub: has && withData.length < w.days.length
          ? `${withData.length}/${w.days.length} gün`
          : `${w.days.length} gün`,
        partial: has && withData.length < w.days.length,
        pending: !has,
        isToday: w.days.some((d) => d.isToday),
        target: has
          ? { sessions: tS, transactions: tT, cr: tCr, revenue: tR }
          : { sessions: fullTarget.sessions, transactions: fullTarget.transactions,
              cr: fullTarget.sessions > 0 ? Math.round((fullTarget.transactions / fullTarget.sessions) * 10000) / 100 : null,
              revenue: fullTarget.revenue },
        fullTarget,
        actual: has ? { sessions: aS, transactions: aT, cr: aCr, revenue: aR } : null,
        hgo: has ? {
          sessions: pct(aS, tS), transactions: pct(aT, tT),
          cr: pct(aCr, tCr), revenue: pct(aR, tR),
        } : null,
        bqTarget: bt,
        bqHgo: has ? {
          sessions: pct(aS, bt.sessions), transactions: pct(aT, bt.transactions),
          cr: pct(aCr, bt.cr), revenue: pct(aR, bt.revenue),
        } : null,
      };
    });
  }, [days, data, bqPct]);

  // Tabloda gösterilen satırlar — günlük ve haftalık aynı hücre düzenini kullanır.
  const rows = useMemo(() => {
    if (view === 'weekly') return weeks;
    return days.map((d) => ({
      key: d.date,
      label: shortDate(d.date),
      sub: d.dowLabel,
      weekend: d.dow >= 6,
      pending: !d.actual,
      isToday: d.isToday,
      target: d.target,
      actual: d.actual,
      hgo: d.hgo,
      bqTarget: d.bqTarget,
      bqHgo: d.bqHgo,
    }));
  }, [view, days, weeks]);

  const exportCsv = () => {
    const pct = data.bqAdjustPct;
    const head = [view === 'weekly' ? 'Hafta' : 'Tarih', view === 'weekly' ? 'Kapsam' : 'Gun'];
    for (const label of ['Session', 'TRX', 'CR', 'Ciro']) {
      head.push(`Hedef ${label}`, `BQ Hedef (-%${pct}) ${label}`,
        `Gerceklesen ${label}`, `HGO ${label}`, `BQ HGO ${label}`);
    }
    const cellsOf = (t, bt, a, h, bh) => METRICS.flatMap((mt) => [
      t?.[mt.k] ?? '', bt?.[mt.k] ?? '', a?.[mt.k] ?? '', h?.[mt.k] ?? '', bh?.[mt.k] ?? '',
    ]);
    const lines = rows.map((d) => [d.label, d.sub, ...cellsOf(d.target, d.bqTarget, d.actual, d.hgo, d.bqHgo)]
      .map((v) => `"${String(v).replace(/"/g, '""')}"`).join(','));
    const totalLine = [`${mtd.days} GUNLUK TOPLAM`, '',
      ...cellsOf(mtd.target, mtd.bqTarget, mtd.actual, mtd.hgo, mtd.bqHgo)]
      .map((v) => `"${v}"`).join(',');
    const monthLine = [`${data.monthLabel} TAMAMI`, '',
      ...cellsOf(monthTarget, data.monthBqTarget, mtd.actual, null, null)]
      .map((v) => `"${v}"`).join(',');
    const csv = [head.join(','), ...lines, totalLine, monthLine].join('\n');
    const url = URL.createObjectURL(new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `high5_hedef_gerceklesen_${data.month}_${view === 'weekly' ? 'haftalik' : 'gunluk'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const th = 'px-3 py-2 text-right text-[11px] font-semibold uppercase tracking-wide';
  const td = 'px-3 py-2 text-right tabular-nums';

  return (
    <div className="p-6 space-y-6">
      <Card className="rounded-2xl overflow-hidden shadow-xl border border-gray-100">
        {/* Başlık */}
        <div className="p-6 bg-white border-b">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-indigo-50 border border-indigo-100 flex items-center justify-center">
                <Target className="w-6 h-6 text-indigo-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Hedef vs Gerçekleşen — Günlük</h1>
                <p className="text-sm text-gray-500 mt-1">
                  {data?.monthLabel || '—'} planı gün gün: oturum, sipariş, dönüşüm oranı ve ciro.
                  {' '}Kaynak: <strong>GA4 BigQuery export</strong>.
                  {selectedBrand?.name ? ` · ${selectedBrand.name}` : ''}
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {(data?.availableMonths?.length || 0) > 1 && (
                <select
                  value={month}
                  onChange={(e) => { setMonth(e.target.value); fetchData(e.target.value); }}
                  className="px-3 py-2 bg-white border rounded-xl text-sm"
                >
                  {data.availableMonths.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
                </select>
              )}
              <label className="flex items-center gap-2 px-3 h-10 bg-white border rounded-xl text-sm text-gray-600 cursor-pointer">
                <input type="checkbox" checked={showBq} onChange={(e) => setShowBq(e.target.checked)} />
                BQ hedefi
                <select
                  value={bqPct}
                  onChange={(e) => { const v = Number(e.target.value); setBqPct(v); fetchData(month, v); }}
                  onClick={(e) => e.preventDefault()}
                  className="bg-transparent text-sm font-semibold text-indigo-700 outline-none"
                >
                  {[0, 5, 10, 15, 20].map((n) => <option key={n} value={n}>−%{n}</option>)}
                </select>
              </label>
              <div className="inline-flex rounded-xl border bg-white p-0.5">
                {[{ k: 'daily', t: 'Günlük' }, { k: 'weekly', t: 'Haftalık' }].map((o) => (
                  <button key={o.k} type="button" onClick={() => setView(o.k)}
                    className={`px-3 h-9 rounded-lg text-sm font-medium transition ${
                      view === o.k ? 'bg-indigo-600 text-white' : 'text-gray-600 hover:bg-gray-50'}`}>
                    {o.t}
                  </button>
                ))}
              </div>
              <button type="button" onClick={exportCsv} disabled={loading || !data}
                className="flex items-center gap-2 px-3 h-10 bg-white border rounded-xl text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50">
                <Download className="w-4 h-4" /> CSV
              </button>
              <button type="button" onClick={() => fetchData(month)} disabled={loading}
                className="flex items-center justify-center w-10 h-10 bg-white border rounded-xl text-gray-600 hover:bg-gray-50 disabled:opacity-50">
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>

          {/* Ay başından bugüne (MTD) */}
          {mtd && (
            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
              <KpiCard
                title={`Oturum — ${mtd.days} günlük`}
                target={mtd.target.sessions} actual={mtd.actual.sessions} hgo={mtd.hgo.sessions}
                bqTarget={mtd.bqTarget?.sessions} bqHgo={mtd.bqHgo?.sessions} bqPct={data.bqAdjustPct}
                sub={<>Ay sonu tahmini <strong className="tabular-nums">{fmtInt(data.projection?.sessions)}</strong> / hedef {fmtInt(monthTarget.sessions)}</>}
              />
              <KpiCard
                title={`Sipariş — ${mtd.days} günlük`}
                target={mtd.target.transactions} actual={mtd.actual.transactions} hgo={mtd.hgo.transactions}
                bqTarget={mtd.bqTarget?.transactions} bqHgo={mtd.bqHgo?.transactions} bqPct={data.bqAdjustPct}
                sub={<>Ay sonu tahmini <strong className="tabular-nums">{fmtInt(data.projection?.transactions)}</strong> / hedef {fmtInt(monthTarget.transactions)}</>}
              />
              <KpiCard
                title={`Dönüşüm oranı — ${mtd.days} günlük`}
                target={mtd.target.cr} actual={mtd.actual.cr} hgo={mtd.hgo.cr}
                bqTarget={mtd.bqTarget?.cr} bqHgo={mtd.bqHgo?.cr} bqPct={data.bqAdjustPct} format={fmtCr}
                sub={<>Hedefe ulaşmak için kalan günlerde <strong>%{((monthTarget.transactions - mtd.actual.transactions) / Math.max(1, monthTarget.sessions - mtd.actual.sessions) * 100).toFixed(2)}</strong> gerekiyor</>}
              />
              <KpiCard
                title={`Ciro — ${mtd.days} günlük`}
                target={mtd.target.revenue} actual={mtd.actual.revenue} hgo={mtd.hgo.revenue}
                bqTarget={mtd.bqTarget?.revenue} bqHgo={mtd.bqHgo?.revenue} bqPct={data.bqAdjustPct} format={fmtMoneyShort}
                sub={<>Ay sonu tahmini <strong className="tabular-nums">{fmtMoneyShort(data.projection?.revenue)}</strong> / hedef {fmtMoneyShort(monthTarget.revenue)}</>}
              />
            </div>
          )}

          <div className="mt-4 flex gap-3 rounded-xl border border-gray-200 bg-gray-50 p-3">
            <Info className="w-4 h-4 text-gray-500 shrink-0 mt-0.5" />
            <div className="text-xs text-gray-600 leading-relaxed">
              <strong>HGO</strong> = hedef gerçekleşme oranı (gerçekleşen ÷ hedef).
              Gerçekleşen değerler <strong>GA4 BigQuery export'undan</strong> — ham olay verisi;
              oturum <code>user_pseudo_id + ga_session_id</code> tekiliyle sayılır, sipariş ve ciro
              <code>purchase</code> olaylarından gelir. Reklam platformlarının kendi raporundan değil,
              o yüzden kanal toplamlarıyla birebir tutmaz. <strong>GA4 arayüzüyle de birebir tutmaz</strong>
              (arayüz oturumu Nisan'dan beri belirgin şekilde daha yüksek okuyor).
              Yarım gün oranı bozduğu için <strong>bugünün satırı boştur</strong>;
              son veri günü {longDate(data?.lastDataDate)}.
              {' '}<strong>BQ hedefi</strong> sütunu, export'un gerçek hacmin bir miktar altını ölçtüğü
              varsayımıyla hedefi <strong>%{data?.bqAdjustPct ?? bqPct} kırpar</strong>
              (hedef × {(1 - (data?.bqAdjustPct ?? bqPct) / 100).toLocaleString('tr-TR')}); yanındaki
              <strong> BQ HGO</strong> bu kırpılmış hedefe göre nerede olduğumuzu gösterir. Oran üstteki
              kutudan değiştirilebilir. ⚠️ <strong>Dönüşüm oranında kırpma etkisizdir</strong> — pay da
              payda da aynı oranda küçüldüğü için CR hedefi {fmtCr(data?.monthTarget?.cr)} olarak kalır.
              {data && monthTarget && (
                <> Ay hedefi toplamı {fmtInt(monthTarget.sessions)} oturum / {fmtInt(monthTarget.transactions)} sipariş
                  / {fmtMoney(monthTarget.revenue)} — plandaki 211.091 oturumla arasındaki 2 oturumluk fark
                  plan tablosundaki yuvarlamadan geliyor.</>
              )}
              {' '}Ay sonu tahmini bugüne kadarki HGO'nun ayın sonuna kadar sürdüğü varsayımıyla hesaplanır.
            </div>
          </div>
        </div>

        {error && <div className="m-4 p-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700">{error}</div>}
        {loading && <div className="p-8 text-center text-gray-400">Yükleniyor…</div>}

        {/* Ciro grafiği */}
        {!loading && chartData.length > 0 && (
          <div className="p-6 border-b">
            <h3 className="text-sm font-semibold text-gray-800 mb-1">GÜN GÜN CİRO: HEDEF vs GERÇEKLEŞEN</h3>
            <p className="text-xs text-gray-500 mb-3">
              Çubuklar o günün cirosu, çizgiler ay başından itibaren birikimli toplam.
              İki çizgi arasındaki açıklık aydaki toplam sapmadır.
            </p>
            <ResponsiveContainer width="100%" height={320}>
              <ComposedChart data={chartData} margin={{ top: 4, right: 8, bottom: 4, left: 4 }}>
                <CartesianGrid vertical={false} stroke="#f1f2f4" />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#6b7280' }} axisLine={false} tickLine={false} />
                <YAxis yAxisId="left" tickFormatter={fmtMoneyShort} tick={{ fontSize: 11, fill: '#6b7280' }} axisLine={false} tickLine={false} width={64} />
                <YAxis yAxisId="right" orientation="right" tickFormatter={fmtMoneyShort} tick={{ fontSize: 11, fill: '#6b7280' }} axisLine={false} tickLine={false} width={64} />
                <RTooltip
                  formatter={(v, n) => [fmtMoney(v), n]}
                  labelFormatter={(l, p) => longDate(p?.[0]?.payload?.date)}
                />
                <Legend verticalAlign="top" height={28} iconType="square" wrapperStyle={{ fontSize: 12 }} />
                <Bar yAxisId="left" dataKey="Hedef ciro" fill="#c7d2fe" maxBarSize={22} />
                <Bar yAxisId="left" dataKey="Gerçekleşen ciro" fill="#4f46e5" maxBarSize={22} />
                <Line yAxisId="right" type="monotone" dataKey="Kümülatif hedef" stroke="#9ca3af" strokeWidth={2} strokeDasharray="4 3" dot={false} />
                <Line yAxisId="right" type="monotone" dataKey="Kümülatif gerçekleşen" stroke="#059669" strokeWidth={2} dot={false} connectNulls={false} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Tablo */}
        {!loading && days.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b">
                  <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500" rowSpan={2}>
                    {view === 'weekly' ? 'Hafta' : 'Tarih'}
                  </th>
                  <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500" rowSpan={2}>
                    {view === 'weekly' ? 'Kapsam' : 'Gün'}
                  </th>
                  {['Session', 'TRX', 'CR', 'Ciro'].map((g) => (
                    <th key={g} className={`${th} text-gray-600 border-l`} colSpan={showBq ? 5 : 3}>{g}</th>
                  ))}
                </tr>
                <tr className="bg-gray-50 border-b">
                  {['', '', '', ''].map((_, i) => (
                    <React.Fragment key={i}>
                      <th className={`${th} text-gray-400 border-l`}>Hedef</th>
                      {showBq && (
                        <th className={`${th} text-indigo-500`} title={`Hedefin BQ karşılığı: hedef − %${data?.bqAdjustPct ?? bqPct}`}>
                          BQ hedefi
                        </th>
                      )}
                      <th className={`${th} text-gray-600`}>Gerç.</th>
                      <th className={`${th} text-gray-400`}>HGO</th>
                      {showBq && <th className={`${th} text-indigo-500`}>BQ HGO</th>}
                    </React.Fragment>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((d) => {
                  const pending = d.pending;
                  return (
                    <tr key={d.key}
                      className={`${d.isToday ? 'bg-indigo-50/50' : pending ? 'bg-gray-50/60' : 'hover:bg-gray-50'}`}>
                      <td className="px-3 py-2 font-medium text-gray-900 whitespace-nowrap">
                        {d.label}
                        {d.isToday && view === 'daily' && <span className="ml-2 text-[10px] font-bold text-indigo-600 uppercase">bugün</span>}
                      </td>
                      <td className={`px-3 py-2 whitespace-nowrap ${d.weekend ? 'text-indigo-600 font-medium' : 'text-gray-500'}`}>
                        {d.sub}
                        {d.partial && (
                          <span className="ml-2 text-[10px] font-semibold text-amber-600 uppercase"
                                title={`Haftanın tamamının hedefi: ${fmtMoney(d.fullTarget?.revenue)}`}>yürüyor</span>
                        )}
                      </td>

                      {METRICS.map((mt) => (
                        <React.Fragment key={mt.k}>
                          <td className={`${td} text-gray-400 border-l`}>{mt.f(d.target?.[mt.k])}</td>
                          {showBq && (
                            <td className={`${td} text-indigo-400`}>{mt.f(d.bqTarget?.[mt.k])}</td>
                          )}
                          <td className={`${td} font-semibold text-gray-900`}>{d.actual ? mt.f(d.actual[mt.k]) : '—'}</td>
                          <td className={`${td} font-bold`}>
                            <span className={`px-1.5 py-0.5 rounded ${hgoChip(d.hgo?.[mt.k])}`}>{fmtHgo(d.hgo?.[mt.k])}</span>
                          </td>
                          {showBq && (
                            <td className={`${td} font-bold`}>
                              <span className={`px-1.5 py-0.5 rounded ${hgoChip(d.bqHgo?.[mt.k])}`}>{fmtHgo(d.bqHgo?.[mt.k])}</span>
                            </td>
                          )}
                        </React.Fragment>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
              {mtd && monthTarget && (
                <tfoot>
                  {/* Bugüne kadarki günlerin toplamı — elma elmaya karşılaştırma */}
                  <tr className="bg-gray-50 border-t-2 border-gray-300 font-semibold">
                    <td className="px-3 py-2 text-gray-700 whitespace-nowrap" colSpan={2}>
                      {mtd.days} GÜNLÜK TOPLAM
                    </td>
                    {METRICS.map((mt) => (
                      <React.Fragment key={mt.k}>
                        <td className={`${td} text-gray-500 border-l`}>{mt.f(mtd.target[mt.k])}</td>
                        {showBq && <td className={`${td} text-indigo-400`}>{mt.f(mtd.bqTarget?.[mt.k])}</td>}
                        <td className={`${td} text-gray-900`}>{mt.f(mtd.actual[mt.k])}</td>
                        <td className={`${td} ${hgoTone(mtd.hgo[mt.k])}`}>{fmtHgo(mtd.hgo[mt.k])}</td>
                        {showBq && <td className={`${td} ${hgoTone(mtd.bqHgo?.[mt.k])}`}>{fmtHgo(mtd.bqHgo?.[mt.k])}</td>}
                      </React.Fragment>
                    ))}
                  </tr>
                  {/* Ayın tamamı — hedef sabit, gerçekleşen henüz kısmi */}
                  <tr className="bg-white border-t font-bold">
                    <td className="px-3 py-2 text-gray-900 whitespace-nowrap" colSpan={2}>
                      {(data.monthLabel || '').toUpperCase()} TAMAMI
                      <div className="text-[10px] font-normal text-gray-400 normal-case">
                        HGO = ay hedefinin bugüne kadar tamamlanan kısmı
                      </div>
                    </td>
                    {METRICS.map((mt) => {
                      const isCr = mt.k === 'cr';
                      return (
                        <React.Fragment key={mt.k}>
                          <td className={`${td} text-gray-700 border-l`}>{mt.f(monthTarget[mt.k])}</td>
                          {showBq && <td className={`${td} text-indigo-400`}>{mt.f(data.monthBqTarget?.[mt.k])}</td>}
                          <td className={`${td} text-gray-900`}>{mt.f(mtd.actual[mt.k])}</td>
                          <td className={`${td} ${isCr ? hgoTone(mtd.hgo.cr) : 'text-gray-400 font-medium'}`}>
                            {fmtHgo(isCr ? mtd.hgo.cr : pctOfMonth(mtd.actual[mt.k], monthTarget[mt.k]))}
                          </td>
                          {showBq && (
                            <td className={`${td} ${isCr ? hgoTone(mtd.bqHgo?.cr) : 'text-indigo-400 font-medium'}`}>
                              {fmtHgo(isCr ? mtd.bqHgo?.cr : pctOfMonth(mtd.actual[mt.k], data.monthBqTarget?.[mt.k]))}
                            </td>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}
      </Card>

      {/* ---------------------------------------------------------------- */}
      {/* Ay ay ciro karşılaştırması — satır ayın günü, sütun ay            */}
      {/* ---------------------------------------------------------------- */}
      <Card className="rounded-2xl overflow-hidden shadow-xl border border-gray-100">
        <div className="p-6 bg-white border-b">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-emerald-50 border border-emerald-100 flex items-center justify-center">
                <CalendarDays className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Ay Ay Ciro — Aynı Takvim Günü</h1>
                <p className="text-sm text-gray-500 mt-1">
                  Satır ayın günü, sütun ay. Geçen ayın 3'ünde kaç paradaydık, bu ayın 3'ünde kaç paradayız —
                  yan yana.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <select
                value={matrixMonths}
                onChange={(e) => { const n = Number(e.target.value); setMatrixMonths(n); fetchMatrix(n); }}
                className="px-3 py-2 bg-white border rounded-xl text-sm"
              >
                {[3, 6, 9, 12].map((n) => <option key={n} value={n}>Son {n} ay</option>)}
              </select>
              <button type="button" onClick={() => fetchMatrix(matrixMonths)} disabled={matrixLoading}
                className="flex items-center justify-center w-10 h-10 bg-white border rounded-xl text-gray-600 hover:bg-gray-50 disabled:opacity-50">
                <RefreshCw className={`w-4 h-4 ${matrixLoading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>

          {matrix && (
            <div className="mt-4 flex gap-3 rounded-xl border border-emerald-100 bg-emerald-50/50 p-3">
              <Info className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
              <div className="text-xs text-gray-600 leading-relaxed">
                Alttaki <strong>ilk {matrix.mtdDay} gün</strong> satırı elma elmaya toplamdır: içinde
                bulunduğumuz ay {matrix.mtdDay}. günde olduğu için geçmiş aylardan da yalnız ilk {matrix.mtdDay} gün
                toplanır. <strong>Ay toplamı</strong> satırında ise ayların tamamı vardır — yürüyen ay
                doğal olarak eksiktir. Tutarlar GA4 BigQuery export cirosu, KDV dahil ve iade düşülmemiş.
              </div>
            </div>
          )}
        </div>

        {matrixError && <div className="m-4 p-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700">{matrixError}</div>}
        {matrixLoading && <div className="p-8 text-center text-gray-400">Yükleniyor…</div>}

        {!matrixLoading && matrix && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b">
                  <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500 sticky left-0 bg-gray-50">Gün</th>
                  {matrix.months.map((m) => (
                    <th key={m.key}
                      className={`px-3 py-2 text-right text-[11px] font-semibold uppercase tracking-wide whitespace-nowrap ${
                        m.isCurrent ? 'text-indigo-700 bg-indigo-50' : 'text-gray-500'}`}>
                      {m.label}
                      {m.isCurrent && <div className="text-[10px] font-normal normal-case text-indigo-500">yürüyen ay</div>}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {matrix.days.map((row) => (
                  <tr key={row.day} className={row.day <= matrix.mtdDay ? 'bg-emerald-50/30' : 'hover:bg-gray-50'}>
                    <td className="px-3 py-1.5 font-medium text-gray-700 tabular-nums sticky left-0 bg-inherit">{row.day}</td>
                    {matrix.months.map((m) => {
                      const c = row.cells[m.key];
                      return (
                        <td key={m.key}
                          className={`px-3 py-1.5 text-right tabular-nums ${
                            !c.exists ? 'text-gray-200'
                              : c.revenue == null ? 'text-gray-300'
                              : m.isCurrent ? 'text-indigo-900 font-semibold bg-indigo-50/40'
                              : 'text-gray-700'}`}>
                          {!c.exists ? '' : c.revenue == null ? '—' : fmtMoney(c.revenue)}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                {/* Elma elmaya: her ayın ilk N günü */}
                <tr className="bg-emerald-50 border-t-2 border-emerald-300 font-bold">
                  <td className="px-3 py-2 text-emerald-900 whitespace-nowrap sticky left-0 bg-emerald-50">
                    İLK {matrix.mtdDay} GÜN
                    <div className="text-[10px] font-normal text-emerald-700/70 normal-case">aynı gün sayısıyla</div>
                  </td>
                  {matrix.months.map((m) => (
                    <td key={m.key} className={`px-3 py-2 text-right tabular-nums ${m.isCurrent ? 'text-indigo-800' : 'text-emerald-900'}`}>
                      {fmtMoney(m.mtdRevenue)}
                      <div className="text-[10px] font-normal text-gray-500">{fmtInt(m.mtdTransactions)} sipariş</div>
                    </td>
                  ))}
                </tr>
                {/* Önceki aya göre değişim */}
                <tr className="bg-white border-t font-semibold">
                  <td className="px-3 py-2 text-gray-600 whitespace-nowrap sticky left-0 bg-white">
                    Önceki aya göre
                  </td>
                  {matrix.months.map((m, i) => {
                    const prev = i > 0 ? matrix.months[i - 1].mtdRevenue : null;
                    const chg = prev > 0 ? ((m.mtdRevenue / prev) - 1) * 100 : null;
                    return (
                      <td key={m.key} className={`px-3 py-2 text-right tabular-nums ${
                        chg == null ? 'text-gray-300' : chg >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>
                        {chg == null ? '—' : `${chg > 0 ? '+' : ''}${chg.toLocaleString('tr-TR', { maximumFractionDigits: 1 })}%`}
                      </td>
                    );
                  })}
                </tr>
                {/* Ayların tamamı */}
                <tr className="bg-gray-50 border-t font-bold">
                  <td className="px-3 py-2 text-gray-800 whitespace-nowrap sticky left-0 bg-gray-50">
                    AY TOPLAMI
                    <div className="text-[10px] font-normal text-gray-500 normal-case">ayın tamamı</div>
                  </td>
                  {matrix.months.map((m) => (
                    <td key={m.key} className={`px-3 py-2 text-right tabular-nums ${m.isCurrent ? 'text-indigo-800' : 'text-gray-900'}`}>
                      {fmtMoney(m.fullRevenue)}
                      <div className="text-[10px] font-normal text-gray-500">
                        {m.isCurrent ? `${m.daysWithData}/${m.daysInMonth} gün` : `${fmtInt(m.fullTransactions)} sipariş`}
                      </div>
                    </td>
                  ))}
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
